import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface CatalogueCardProps {
  catalogue: {
    _id?: string;
    name: string;
    description?: string;
    productCount?: number;
    categoryCount?: number;
    isActive?: boolean;
    currency?: string;
    locale?: string;
  };
  onPress?: (catalogueId: string) => void;
  onEdit?: (catalogueId: string) => void;
  onDelete?: (catalogueId: string) => void;
  onManageChannels?: (catalogueId: string) => void;
  showActions?: boolean;
  containerStyle?: any;
  /** Multi-select mode ("Assign Products" bulk action) — tapping the card
   * toggles selection instead of navigating. */
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (catalogueId: string) => void;
}

export const CatalogueCard: React.FC<CatalogueCardProps> = ({
  catalogue,
  onPress,
  onEdit,
  onDelete,
  onManageChannels,
  showActions = true,
  containerStyle,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const handlePress = () => {
    if (!catalogue._id) return;
    if (selectionMode) {
      onToggleSelect?.(catalogue._id);
    } else {
      onPress?.(catalogue._id);
    }
  };

  const handleEdit = (e: any) => {
    e.stopPropagation();
    if (catalogue._id && onEdit) {
      onEdit(catalogue._id);
    }
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    if (catalogue._id && onDelete) {
      onDelete(catalogue._id);
    }
  };

  const handleManageChannels = (e: any) => {
    e.stopPropagation();
    if (catalogue._id && onManageChannels) {
      onManageChannels(catalogue._id);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle, selectionMode && isSelected && styles.containerSelected]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <View style={styles.header}>
          {selectionMode && (
            <Ionicons
              name={isSelected ? 'checkmark-circle' : 'ellipse-outline'}
              size={22}
              color={isSelected ? colors.primary : colors.text.disabled}
              style={styles.selectionCheckbox}
            />
          )}
          <View style={styles.iconContainer}>
            <Ionicons name="cube-outline" size={24} color={colors.primary} />
          </View>
          <View style={styles.infoContainer}>
            <View style={styles.titleRow}>
              <Text style={styles.name} numberOfLines={1}>
                {catalogue.name}
              </Text>
              {catalogue.isActive === false && (
                <View style={styles.inactiveBadge}>
                  <Text style={styles.inactiveBadgeText}>Inactive</Text>
                </View>
              )}
            </View>
            {catalogue.description && (
              <Text style={styles.description} numberOfLines={2}>
                {catalogue.description}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.actionsRow}>
          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Ionicons name="cube" size={14} color={colors.textLight} style={styles.statIcon} />
              <Text style={styles.statText}>
                {catalogue.productCount || 0}
              </Text>
            </View>
            {catalogue.categoryCount !== undefined && catalogue.categoryCount > 0 && (
              <View style={styles.statItem}>
                <Ionicons name="folder" size={14} color={colors.textLight} style={styles.statIcon} />
                <Text style={styles.statText}>
                  {catalogue.categoryCount}
                </Text>
              </View>
            )}
          </View>

          {showActions && (
            <View style={styles.actionsContainer}>
              {onManageChannels && (
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleManageChannels}
                  activeOpacity={0.7}
                >
                  <Ionicons name="link-outline" size={18} color={colors.primary} />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleEdit}
                activeOpacity={0.7}
              >
                <Ionicons name="create-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleDelete}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    overflow: 'hidden',
    marginBottom: spacing.md,
    borderWidth: 2,
    borderColor: 'transparent',
    ...designTokens.shadows.sm,
  },
  containerSelected: {
    borderColor: colors.primary,
  },
  selectionCheckbox: {
    marginRight: spacing.sm,
    marginTop: 2,
  },
  content: {
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: withAlpha(colors.primary, 0.08),
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
    flexShrink: 0,
  },
  infoContainer: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 2,
    gap: spacing.xs,
  },
  name: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
  },
  inactiveBadge: {
    backgroundColor: withAlpha(colors.warning, 0.13),
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  inactiveBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    lineHeight: 18,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: withAlpha(colors.border, 0.25),
    gap: spacing.sm,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    backgroundColor: colors.lightGray,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  statIcon: {
    marginRight: 0,
  },
  statText: {
    fontSize: typography.fontSize.xs,
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
    marginLeft: 0,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.xs,
    flexShrink: 0,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

